import React, { createContext, useEffect } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import { useContentCardsNotificationsExperiment } from 'experiments/use-content-cards-notifications';
import Braze from 'utils/braze';
import { isNative } from 'utils/environment';

import { BrazeCssBugOverride } from './braze-css-bug-override';
import useBrazeContext from './hooks/use-braze-context';
import { IBrazeContext } from './types';

export const BrazeContext = createContext<IBrazeContext>({} as IBrazeContext);

/**
 * The default rate limit for manually refreshing the feed is 3 calls per 10 minutes per device
 * to prevent performance degradation and errors.
 * @see https://www.braze.com/docs/developer_guide/customization_guides/content_cards/customizing_feed/?tab=web&redirected=true
 */
const REFRESHING_FEED_INTERVAL = 4 * 60 * 1000; // Set to 4 min

export const BrazeProvider = ({ children }: IBaseProps) => {
  const ctxValue = useBrazeContext();
  const { isExperimentEnabled: enableContentCards } = useContentCardsNotificationsExperiment();
  const { setIsInAppMessageOpen } = ctxValue;

  useEffect(() => {
    if (isNative) {
      Braze.subscribeToInAppMessage(true, event => {
        setIsInAppMessageOpen(true);
      });
    }
  }, [setIsInAppMessageOpen]);

  useEffect(() => {
    if (!enableContentCards) {
      return;
    }

    const intervalId = setInterval(() => {
      Braze.requestContentCardsRefresh();
    }, REFRESHING_FEED_INTERVAL);

    return () => clearInterval(intervalId);
  }, [enableContentCards]);

  return (
    <BrazeContext.Provider value={ctxValue}>
      <BrazeCssBugOverride />
      {children}
    </BrazeContext.Provider>
  );
};
